// oxlint-disable-next-line no-restricted-imports
import { z } from 'zod';
import { createEnv } from '@t3-oss/env-nextjs';

export const zEnv = z.enum(['development', 'staging', 'production']);

/**
 * Apply database options to database connection string.
 * @param {string} connectionString database connection string
 * @returns {string}
 */
function applyDatabaseOptions(connectionString) {
  try {
    const urlObj = new URL(connectionString);
    urlObj.searchParams.set('pool_timeout', '300'); // 5 minutes
    return urlObj.toString();
  } catch {
    // This is probably `new URL` throwing an error. We don't transform invalid URLs - let Zod validation handle them.
    // It will provide better feedback to the developer than we could here.
    return connectionString;
  }
}

// Prisma loads the DATABASE_URL from process.env.DATABASE_URL by default, so we need to set it here.
// To ensure the options we set in `env.mjs` are respected.
if (process.env.DATABASE_URL) {
  process.env.DATABASE_URL = applyDatabaseOptions(process.env.DATABASE_URL);
}

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    // Master API key for AI Proxy (from Secrets Manager, used as fallback for scheduled tests)
    AI_PROXY_API_KEY: z
      .string()
      .optional()
      .transform((val) => {
        // Strip surrounding quotes if present (Docker --env-file doesn't strip quotes)
        // This allows using quotes in .env files to prevent # from being treated as a comment
        if (!val) {
          return val;
        }
        if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
          return val.slice(1, -1);
        }
        return val;
      }),
    // Temporary API key for AI Proxy (injected by App Server at container start)
    // Takes precedence over AI_PROXY_API_KEY when present
    AI_PROXY_TEMP_API_KEY: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return val;
        }
        if ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'"))) {
          return val.slice(1, -1);
        }
        return val;
      }),
    AI_PROXY_URL: z.url().optional(),
    ANTHROPIC_API_KEY: z.string().optional(),
    AWS_ACCESS_KEY_ID: z.string(),
    BROWSERBASE_API_KEY: z.string().optional(),
    BROWSERBASE_PROJECT_ID: z.string().optional(),
    // We pass this to Claude Code SDK
    // In other parts for bedrock, we auth using '@aws/credential-providers'
    AWS_BEARER_TOKEN_BEDROCK: z.string().optional(),
    AWS_REGION: z.string(),
    AWS_SECRET_ACCESS_KEY: z.string(),
    // Used for backend self communication verification across cloud providers
    BACKEND_META_KEY: z.string().min(1),
    BILLING_DLQ_URL: z.string().optional(),
    BILLING_QUEUE_URL: z.string().optional(),
    // Cloudflare
    // This token must have the following scopes:
    // - Zone > Cache Purge > Purge
    CLOUDFLARE_API_TOKEN: z.string().min(1).optional(),
    CLOUDFLARE_ZONE_ID: z.string().min(1).optional(),
    DAILY_API_KEY: z.string().min(1),
    DATABASE_URL: z.url(),
    // Enable recorder trace uploads (for debugging AI agent test generation)
    ENABLE_RECORDER_TRACES: z
      .string()
      .transform((x) => !!x)
      .optional(),
    ENVIRONMENT: zEnv,
    FIRECRAWL_API_KEY: z.string().min(1),
    GEMINI_API_KEY: z.string().optional(),
    // Note: Can't start secrets with the name GITHUB due to using GitHub
    // Note: we base64 encode this, so we have to decode that when we use it
    GITHB_APP_CLIENT_SECRET: z.string().optional(),
    GITHB_APP_INTERNAL_PRIVATE_KEY: z.string().optional(),
    GITHB_APP_PRIVATE_KEY: z.string().optional(),
    GITHB_APP_WEBHOOK_SECRET: z.string().optional(),
    // GitLab OAuth
    GITLAB_APP_CLIENT_SECRET: z.string().optional(),
    GOOGLE_CLOUD_ANDROID_EMULATOR_PROJECT_ID: z.string().min(1),
    GOOGLE_CLOUD_CLIENT_EMAIL: z.string().min(1),
    GOOGLE_CLOUD_PRIVATE_KEY: z.string().min(1),
    GOOGLE_CLOUD_VERTEX_PROJECT_ID: z.string().min(1),
    GOOGLE_CLOUD_VERTEX_CLIENT_EMAIL: z.string().min(1),
    GOOGLE_CLOUD_VERTEX_PRIVATE_KEY: z.string().min(1),
    IS_DOCKER: z
      .string()
      .transform((x) => x === '1')
      .optional(),
    JIRA_CLIENT_SECRET: z.string().optional(),
    // Kernel.sh browser infrastructure API key
    KERNEL_API_KEY: z.string().optional(),
    // Kernel.sh custom proxy ID for browser sessions
    KERNEL_PROXY_ID: z.string().optional(),
    VERCEL_SANDBOX_PROJECT_ID: z.string().optional(),
    VERCEL_SANDBOX_SNAPSHOT_ID: z.string().optional(),
    VERCEL_SANDBOX_TEAM_ID: z.string().optional(),
    VERCEL_SANDBOX_TIMEOUT_MS: z
      .string()
      .transform((val) => parseInt(val, 10))
      .refine((val) => !isNaN(val) && val > 0, {
        message: 'VERCEL_SANDBOX_TIMEOUT_MS must be a positive number',
      })
      .optional(),
    VERCEL_SANDBOX_TOKEN: z.string().optional(),
    LANGSMITH_API_KEY: z.string().optional(),
    LANGSMITH_PROJECT: z.string().default('stably-dev'),
    LANGSMITH_TRACING: z.string().optional(),
    LINEAR_CLIENT_SECRET: z.string().optional(),
    NOTION_CLIENT_SECRET: z.string().optional(),
    OPENAI_API_KEY: z.string().optional(),
    // Prisma transaction timeout in milliseconds
    PRISMA_TRANSACTION_TIMEOUT: z
      .string()
      .transform((val) => parseInt(val, 10))
      .refine((val) => !isNaN(val) && val > 0, {
        message: 'PRISMA_TRANSACTION_TIMEOUT must be a positive number',
      })
      .optional(),
    PROPELAUTH_API_KEY: z.string().min(1),
    PROPELAUTH_VERIFIER_KEY: z.string().min(1),
    PROPELAUTH_WEBHOOK_SECRET: z.string().optional(),
    // Residential proxy
    PROXY_PASSWORD: z.string().optional(),
    PROXY_SERVER: z.string().optional(),
    PROXY_USERNAME: z.string().optional(),
    R2_ACCESS_KEY_ID: z.string().min(1),
    R2_SECRET_ACCESS_KEY: z.string().min(1),
    RUNNER_V2_QUEUE_URL: z.string().optional(),
    SLACK_CLIENT_SECRET: z.string(),
    SLACK_INTERNAL_BOT_TOKEN: z.string().optional(),
    STABLY_API_KEY_JWT_SIGNING_KEY: z.string().optional(),
    // Stably API URL for browser-minutes billing
    STABLY_API_URL: z.string().optional(),
    STABLY_LFS_BASE_URL: z.string().optional(),
    // Email API
    STABLY_EMAIL_API_KEY: z.string().min(1),
    STABLY_EMAIL_DOMAIN: z.string(),
    STATIC_IP_PROXY_PASSWORD: z.string().optional(),
    STATIC_IP_PROXY_SERVER: z.string().optional(),
    STATIC_IP_PROXY_USERNAME: z.string().optional(),
    STRIPE_WEBHOOK_SECRET: z.string().optional(),
    STRIPE_SECRET_KEY: z.string().optional(),
    // AI Proxy
    // Whether to use AI proxy (LiteLLM) or direct provider access
    USE_AI_PROXY: z
      .string()
      .transform((x) => x === 'true')
      .optional(),
    USE_FARGATE_RECORDER: z
      .string()
      .transform((x) => !!x)
      .optional(),
    // Static IP proxy
    USE_STATIC_IP_PROXY: z
      .string()
      .transform((x) => !!x)
      .optional(),
    VALKEY_URL: z.string().optional(),
    // ClickHouse Cloud
    CLICKHOUSE_URL: z.string().optional(),
    CLICKHOUSE_USER: z.string().optional(),
    CLICKHOUSE_PASSWORD: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_AUTH_URL: z.url(),
    NEXT_PUBLIC_BUCKET_ORIGIN: z.url(),
    NEXT_PUBLIC_BUCKET_PLAYWRIGHT: z.url(),
    NEXT_PUBLIC_GENERATION_HTTP_URL: z.url().optional(),
    NEXT_PUBLIC_GITHUB_APP_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_GITHUB_APP_ID: z.string().optional(),
    NEXT_PUBLIC_GITHUB_APP_NAME: z.string().optional(),
    // GitLab OAuth
    NEXT_PUBLIC_GITLAB_APP_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_HYPERDX_API_KEY: z.string().optional(),
    NEXT_PUBLIC_JIRA_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_LINEAR_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_MOBILE_RECORDER_HOST: z.string().optional(),
    NEXT_PUBLIC_NOTION_CLIENT_ID: z.string().optional(),
    NEXT_PUBLIC_OUTAGE_BANNER_MESSAGE: z.string().optional(),
    NEXT_PUBLIC_PROPELAUTH_PROJECT_ID: z.string().min(1).optional(),
    NEXT_PUBLIC_PROXY_WS_URL: z.url().optional(),
    NEXT_PUBLIC_STABLY_API_URL: z.string().url().optional(),
    NEXT_PUBLIC_STABLY_EMAIL_DOMAIN: z.string(),
    NEXT_PUBLIC_STRIPE_PAYMENTLINK_ID: z.string().optional(),
    NEXT_PUBLIC_VERCEL_ENV: z.enum(['development', 'preview', 'production']).optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    AI_PROXY_API_KEY: process.env.AI_PROXY_API_KEY,
    AI_PROXY_TEMP_API_KEY: process.env.AI_PROXY_TEMP_API_KEY,
    AI_PROXY_URL: process.env.AI_PROXY_URL,
    ANTHROPIC_API_KEY: process.env.ANTHROPIC_API_KEY,
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    BILLING_DLQ_URL: process.env.BILLING_DLQ_URL,
    BILLING_QUEUE_URL: process.env.BILLING_QUEUE_URL,
    BROWSERBASE_API_KEY: process.env.BROWSERBASE_API_KEY,
    BROWSERBASE_PROJECT_ID: process.env.BROWSERBASE_PROJECT_ID,
    AWS_BEARER_TOKEN_BEDROCK: process.env.AWS_BEARER_TOKEN_BEDROCK,
    AWS_REGION: process.env.AWS_REGION,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    BACKEND_META_KEY: process.env.BACKEND_META_KEY,
    CLICKHOUSE_URL: process.env.CLICKHOUSE_URL,
    CLICKHOUSE_USER: process.env.CLICKHOUSE_USER,
    CLICKHOUSE_PASSWORD: process.env.CLICKHOUSE_PASSWORD,
    CLOUDFLARE_API_TOKEN: process.env.CLOUDFLARE_API_TOKEN,
    CLOUDFLARE_ZONE_ID: process.env.CLOUDFLARE_ZONE_ID,
    DAILY_API_KEY: process.env.DAILY_API_KEY,
    DATABASE_URL: process.env.DATABASE_URL,
    ENABLE_RECORDER_TRACES: process.env.ENABLE_RECORDER_TRACES,
    ENVIRONMENT: process.env.ENVIRONMENT || process.env.NODE_ENV,
    FIRECRAWL_API_KEY: process.env.FIRECRAWL_API_KEY,
    GEMINI_API_KEY: process.env.GEMINI_API_KEY,
    GOOGLE_CLOUD_ANDROID_EMULATOR_PROJECT_ID: process.env.GOOGLE_CLOUD_ANDROID_EMULATOR_PROJECT_ID,
    GOOGLE_CLOUD_CLIENT_EMAIL: process.env.GOOGLE_CLOUD_CLIENT_EMAIL,
    GOOGLE_CLOUD_PRIVATE_KEY: process.env.GOOGLE_CLOUD_PRIVATE_KEY,
    GOOGLE_CLOUD_VERTEX_PROJECT_ID: process.env.GOOGLE_CLOUD_VERTEX_PROJECT_ID,
    GOOGLE_CLOUD_VERTEX_CLIENT_EMAIL: process.env.GOOGLE_CLOUD_VERTEX_CLIENT_EMAIL,
    GOOGLE_CLOUD_VERTEX_PRIVATE_KEY: process.env.GOOGLE_CLOUD_VERTEX_PRIVATE_KEY,
    GITHB_APP_CLIENT_SECRET: process.env.GITHB_APP_CLIENT_SECRET,
    GITHB_APP_INTERNAL_PRIVATE_KEY: process.env.GITHB_APP_INTERNAL_PRIVATE_KEY,
    GITHB_APP_PRIVATE_KEY: process.env.GITHB_APP_PRIVATE_KEY,
    GITHB_APP_WEBHOOK_SECRET: process.env.GITHB_APP_WEBHOOK_SECRET,
    GITLAB_APP_CLIENT_SECRET: process.env.GITLAB_APP_CLIENT_SECRET,
    IS_DOCKER: process.env.IS_DOCKER,
    JIRA_CLIENT_SECRET: process.env.JIRA_CLIENT_SECRET,
    KERNEL_API_KEY: process.env.KERNEL_API_KEY,
    KERNEL_PROXY_ID: process.env.KERNEL_PROXY_ID,
    VERCEL_SANDBOX_PROJECT_ID: process.env.VERCEL_SANDBOX_PROJECT_ID,
    VERCEL_SANDBOX_SNAPSHOT_ID: process.env.VERCEL_SANDBOX_SNAPSHOT_ID,
    VERCEL_SANDBOX_TEAM_ID: process.env.VERCEL_SANDBOX_TEAM_ID,
    VERCEL_SANDBOX_TIMEOUT_MS: process.env.VERCEL_SANDBOX_TIMEOUT_MS,
    VERCEL_SANDBOX_TOKEN: process.env.VERCEL_SANDBOX_TOKEN,
    LANGSMITH_API_KEY: process.env.LANGSMITH_API_KEY,
    LANGSMITH_PROJECT: process.env.LANGSMITH_PROJECT,
    LANGSMITH_TRACING: process.env.LANGSMITH_TRACING,
    LINEAR_CLIENT_SECRET: process.env.LINEAR_CLIENT_SECRET,
    NEXT_PUBLIC_AUTH_URL: process.env.NEXT_PUBLIC_AUTH_URL,
    NEXT_PUBLIC_BUCKET_ORIGIN: process.env.NEXT_PUBLIC_BUCKET_ORIGIN,
    NEXT_PUBLIC_BUCKET_PLAYWRIGHT: process.env.NEXT_PUBLIC_BUCKET_PLAYWRIGHT,
    NEXT_PUBLIC_GENERATION_HTTP_URL: process.env.NEXT_PUBLIC_GENERATION_HTTP_URL,
    NEXT_PUBLIC_GITHUB_APP_CLIENT_ID: process.env.NEXT_PUBLIC_GITHUB_APP_CLIENT_ID,
    NEXT_PUBLIC_GITHUB_APP_ID: process.env.NEXT_PUBLIC_GITHUB_APP_ID,
    NEXT_PUBLIC_GITHUB_APP_NAME: process.env.NEXT_PUBLIC_GITHUB_APP_NAME,
    NEXT_PUBLIC_GITLAB_APP_CLIENT_ID: process.env.NEXT_PUBLIC_GITLAB_APP_CLIENT_ID,
    NEXT_PUBLIC_HYPERDX_API_KEY: process.env.NEXT_PUBLIC_HYPERDX_API_KEY,
    NEXT_PUBLIC_JIRA_CLIENT_ID: process.env.NEXT_PUBLIC_JIRA_CLIENT_ID,
    NEXT_PUBLIC_LINEAR_CLIENT_ID: process.env.NEXT_PUBLIC_LINEAR_CLIENT_ID,
    NEXT_PUBLIC_MOBILE_RECORDER_HOST: process.env.NEXT_PUBLIC_MOBILE_RECORDER_HOST,
    NEXT_PUBLIC_NOTION_CLIENT_ID: process.env.NEXT_PUBLIC_NOTION_CLIENT_ID,
    NEXT_PUBLIC_OUTAGE_BANNER_MESSAGE: process.env.NEXT_PUBLIC_OUTAGE_BANNER_MESSAGE,
    NEXT_PUBLIC_PROPELAUTH_PROJECT_ID: process.env.NEXT_PUBLIC_PROPELAUTH_PROJECT_ID,
    NEXT_PUBLIC_PROXY_WS_URL: process.env.NEXT_PUBLIC_PROXY_WS_URL,
    NEXT_PUBLIC_STABLY_API_URL: process.env.NEXT_PUBLIC_STABLY_API_URL,
    NEXT_PUBLIC_STABLY_EMAIL_DOMAIN: process.env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN,
    NEXT_PUBLIC_STRIPE_PAYMENTLINK_ID: process.env.NEXT_PUBLIC_STRIPE_PAYMENTLINK_ID,
    NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
    NOTION_CLIENT_SECRET: process.env.NOTION_CLIENT_SECRET,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    PRISMA_TRANSACTION_TIMEOUT: process.env.PRISMA_TRANSACTION_TIMEOUT,
    PROPELAUTH_API_KEY: process.env.PROPELAUTH_API_KEY,
    PROPELAUTH_VERIFIER_KEY: process.env.PROPELAUTH_VERIFIER_KEY,
    PROPELAUTH_WEBHOOK_SECRET: process.env.PROPELAUTH_WEBHOOK_SECRET,
    PROXY_PASSWORD: process.env.PROXY_PASSWORD,
    PROXY_SERVER: process.env.PROXY_SERVER,
    PROXY_USERNAME: process.env.PROXY_USERNAME,
    R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID,
    R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY,
    RUNNER_V2_QUEUE_URL: process.env.RUNNER_V2_QUEUE_URL,
    SLACK_CLIENT_SECRET: process.env.SLACK_CLIENT_SECRET,
    SLACK_INTERNAL_BOT_TOKEN: process.env.SLACK_INTERNAL_BOT_TOKEN,
    STABLY_API_KEY_JWT_SIGNING_KEY: process.env.STABLY_API_KEY_JWT_SIGNING_KEY,
    STABLY_API_URL: process.env.STABLY_API_URL,
    STABLY_LFS_BASE_URL: process.env.STABLY_LFS_BASE_URL,
    STABLY_EMAIL_API_KEY: process.env.STABLY_EMAIL_API_KEY,
    STABLY_EMAIL_DOMAIN: process.env.STABLY_EMAIL_DOMAIN,
    STATIC_IP_PROXY_PASSWORD: process.env.STATIC_IP_PROXY_PASSWORD,
    STATIC_IP_PROXY_SERVER: process.env.STATIC_IP_PROXY_SERVER,
    STATIC_IP_PROXY_USERNAME: process.env.STATIC_IP_PROXY_USERNAME,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    USE_AI_PROXY: process.env.USE_AI_PROXY,
    USE_FARGATE_RECORDER: process.env.USE_FARGATE_RECORDER,
    USE_STATIC_IP_PROXY: process.env.USE_STATIC_IP_PROXY,
    VALKEY_URL: process.env.VALKEY_URL,
  },

  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
});
